/**
 * dashboardChildren — Extraktion der `/dashboard`-`children`-Routen aus
 * `App.tsx` (M-UX-1.1, `architecture-ux-restructure.md` §4.2).
 *
 * `buildDashboardChildren()` ist verhaltensidentisch zur vorherigen
 * inline-Definition in `App.tsx:203-397` (identische Elemente, identische
 * Flag-/Beta-Gates, identisches `AdvisorHidden`-Wrapping, identisches
 * `errorElement`). Die Ableitung läuft über `DASHBOARD_ROUTE_PATHS`
 * (`routes/dashboardRoutePaths.ts`, firebase-frei) als EINE Quelle: jeder
 * Pfad aus der Liste wird über `ELEMENT_BY_PATH` auf sein `element` gemappt.
 * Ist ein Pfad in `DASHBOARD_ROUTE_PATHS`, aber fehlt in `ELEMENT_BY_PATH`,
 * wirft `buildDashboardChildren()` sofort (Fail-Fast statt stiller 404) —
 * das kann nur bei einem internen Wartungsfehler dieser Datei passieren
 * (Pfad-Liste und Element-Map laufen bewusst synchron), nie durch Nutzer-Input.
 *
 * WICHTIG: Diese Datei importiert Page-Komponenten und zieht damit transitiv
 * `firebaseConfig` — sie ist NICHT für den Registry-Test geeignet (s.
 * `routes/dashboardRoutePaths.ts` Modul-Kopf). Der Test importiert
 * ausschließlich die firebase-freie Pfad-Liste.
 *
 * @module routes/dashboardChildren
 */

import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import CockpitPage from '../pages/CockpitPage';
import InvoiceDashboard from '../pages/InvoiceDashboard';
import OfferDashboard from '../pages/OfferDashboard';
import XRechnungDashboard from '../pages/XRechnungDashboard';
import InvoiceArchive from '../components/InvoiceArchive';
import OfferArchive from '../components/OfferArchive';
import SubscriptionPage from '../pages/SubscriptionPage';
import SubscriptionSuccessPage from '../pages/SubscriptionSuccessPage';
import SubscriptionCancelPage from '../pages/SubscriptionCancelPage';
import AccountPage from '../pages/AccountPage';
import AdvisorPortalPage from '../pages/AdvisorPortalPage';
import AdvisorCockpitPage from '../pages/AdvisorCockpitPage';
import AdvisorDatevPage from '../pages/AdvisorDatevPage';
import MandantenPage from '../pages/MandantenPage';
import ClientInvitePage from '../pages/ClientInvitePage';
import ClientInviteAcceptPage from '../pages/ClientInviteAcceptPage';
import CustomerManagementPage from '../pages/CustomerManagementPage';
import ProductManagementPage from '../pages/ProductManagementPage';
import WorkflowManagementPage from '../pages/WorkflowManagementPage';
import WorkflowDetailPage from '../pages/WorkflowDetailPage';
import PdfSettingsPage from '../pages/PdfSettingsPage';
import ReceivedInvoicesPage from '../pages/ReceivedInvoicesPage';
import InboxPage from '../pages/InboxPage';
import UnifiedInboxPage from '../pages/UnifiedInboxPage';
import OpenItemsPage from '../pages/OpenItemsPage';
import ExpensesPage from '../pages/ExpensesPage';
import RecurringInvoicesPage from '../pages/RecurringInvoicesPage';
import DatevExportPage from '../pages/DatevExportPage';
import UmsatzsteuerPage from '../pages/UmsatzsteuerPage';
import GobdExportPage from '../pages/GobdExportPage';
import GobdDocPage from '../pages/GobdDocPage';
import ReceiptScanPage from '../pages/ReceiptScanPage';
import MessagesPage from '../pages/MessagesPage';
import BankReconciliationPage from '../pages/BankReconciliationPage';
import TravelExpensesPage from '../pages/TravelExpensesPage';
import ErrorBoundary from '../components/ErrorBoundary';
import AdvisorLandingGate from '../components/advisor/AdvisorLandingGate';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import { canUseUnifiedInbox } from '../config/unifiedInboxAccess';
import { useAuth } from '../context/AuthContext';
import { resolveTier } from '../config/entitlements';
import { canUseKanzleiCockpit } from '../config/kanzleiAccess';
import { canUseCockpit } from '../config/cockpitAccess';
import { DASHBOARD_ROUTE_PATHS } from './dashboardRoutePaths';

// M-UX-3.2 (`plan-ux-3.2-advisor.md` §4.1): geteiltes Flag mit M-UX-3.1/3.2 —
// gatet, ob `AdvisorHidden` auf `/dashboard/cockpit` statt `/dashboard/archiv`
// umleitet. Reines globales Env-Flag, Modul-Ladezeit-Read (anders als
// `canUseUnifiedInbox` unten, das per-Nutzer zur RENDER-Zeit über `useAuth()`
// entscheidet, s. `EingangsrechnungenRoute`/`PosteingangRoute`/`BelegeRoute`).
const UX_SHELL_ENABLED = process.env.REACT_APP_UX_SHELL_ENABLED === 'true';

/**
 * fix-advisor-nav-curation: Route-Guard für Routen, die im Advisor-Kontext
 * (`activeRole === 'advisor'`) fürs Nav ausgeblendet sind. Deep-Links/Direkt-
 * Navigation auf diese Routen leiten den Advisor um, statt eine für den
 * Steuerberater irrelevante/leere Seite zu zeigen. Rein UX-Robustheit —
 * keine Sicherheitsgrenze, die harte Sperre bleibt bei Firestore-Rules/PHP.
 * Für Nicht-Advisor (owner/member auf eigenem Tenant bzw. Flag off) ist
 * `useIsReadOnly()` immer `false` ⇒ children rendern unverändert (Null-Delta).
 *
 * M-UX-3.2 (`plan-ux-3.2-advisor.md` §4.1, E5): das Redirect-Ziel ist
 * konditional geworden — dies ist die EINE Stelle, an der die
 * Advisor-Landing berechnet wird (Single Source; `TenantSwitcher.tsx`
 * navigiert nach einem Tenant-Wechsel nur noch auf `/dashboard` und
 * überlässt diesem Guard das eigentliche Ziel, §4.2).
 * - `UX_SHELL_ENABLED` off ⇒ weiterhin `/dashboard/archiv` (Null-Delta zum
 *   Vor-3.2-Stand — 1:1 aus `App.tsx` übernommenes Verhalten).
 * - `UX_SHELL_ENABLED` on + Loop-B-Beta (`isLoopBBeta`) ⇒ `/dashboard/cockpit`
 *   (Exception-First-Cockpit als Landing).
 * - `UX_SHELL_ENABLED` on + Nicht-Beta ⇒ weiterhin `/dashboard/archiv`
 *   (Cockpit ist selbst Loop-B-gated — kein Leerseiten-Redirect).
 */
export const AdvisorHidden: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const advisor = useIsReadOnly();
  const { currentUser, sellerData } = useAuth();
  if (advisor) {
    const target =
      UX_SHELL_ENABLED && canUseKanzleiCockpit(currentUser?.uid, sellerData)
        ? '/dashboard/cockpit'
        : '/dashboard/archiv';
    return <Navigate to={target} replace />;
  }
  return <>{children}</>;
};

// ── fix-unified-inbox-beta-gate ──────────────────────────────────────────
// Die drei Alt-Routen (`/dashboard/eingangsrechnungen`, `/dashboard/posteingang`,
// `/dashboard/belege`) redirecten auf die Unified Inbox, wenn der AKTUELLE
// Nutzer sie sehen darf. Das ist jetzt eine PER-NUTZER-Entscheidung
// (`canUseUnifiedInbox(effectiveScopeUid, resolveTier(sellerData))`), die zur RENDER-Zeit via
// `useAuth()` getroffen werden muss — anders als die alte, rein globale
// `UNIFIED_INBOX_ENABLED`-Auswertung zur Modul-Ladezeit (die konnte direkt in
// `ELEMENT_BY_PATH` inline stehen). Jede Route bekommt daher eine eigene
// kleine Wrapper-Komponente statt einer Inline-Bedingung in der Fabrik-Funktion.
const EingangsrechnungenRoute: React.FC = () => {
  const { effectiveScopeUid, sellerData } = useAuth();
  return canUseUnifiedInbox(effectiveScopeUid, resolveTier(sellerData)) ? (
    <Navigate to="/dashboard/eingaenge?stream=upload" replace />
  ) : (
    <ReceivedInvoicesPage />
  );
};

const PosteingangRoute: React.FC = () => {
  const { effectiveScopeUid, sellerData } = useAuth();
  return canUseUnifiedInbox(effectiveScopeUid, resolveTier(sellerData)) ? (
    <Navigate to="/dashboard/eingaenge?stream=email" replace />
  ) : (
    <InboxPage />
  );
};

const BelegeRoute: React.FC = () => {
  const { effectiveScopeUid, sellerData } = useAuth();
  return canUseUnifiedInbox(effectiveScopeUid, resolveTier(sellerData)) ? (
    <Navigate to="/dashboard/eingaenge?stream=scan" replace />
  ) : (
    <ReceiptScanPage />
  );
};

/**
 * `/dashboard/eingaenge` selbst: die Route-Registrierung
 * (`routes/dashboardRoutePaths.ts`, `UNIFIED_INBOX_ROUTE_ENABLED`) bleibt
 * bewusst GROB (registriert, sobald irgendein Nutzer per Flag/Beta-Allowlist
 * Zugriff haben könnte) — ohne diesen Gate könnte deshalb auch ein NICHT
 * berechtigter Nutzer per Direkt-Navigation die Seite erreichen. Dieser
 * Wrapper trifft die feine Per-Nutzer-Entscheidung und leitet nicht
 * Berechtigte auf die immer registrierte Alt-Route um (kein Leerseiten-Reach,
 * kein versehentlicher Beta-Preview für Nicht-Zielgruppen). Für die Zielgruppe
 * (Inbound- ODER Scanner-Beta ODER globales Flag) unverändertes Verhalten.
 */
const UnifiedInboxRoute: React.FC = () => {
  const { effectiveScopeUid, sellerData } = useAuth();
  if (!canUseUnifiedInbox(effectiveScopeUid, resolveTier(sellerData))) {
    return <Navigate to="/dashboard/eingangsrechnungen" replace />;
  }
  return <UnifiedInboxPage />;
};

/**
 * `/dashboard`-Index-Entscheid (architecture-cockpit-route-split.md §5.2, E3).
 * Wählt zur RENDER-Zeit — NICHT zur Modul-Ladezeit — zwischen Owner-Cockpit und
 * Rechnungsformular. Muster `UnifiedInboxRoute` (Route-Registrierung grob,
 * Per-Nutzer-Entscheidung im Render).
 *
 * Bewusste Entscheidungen (§5.2):
 * - `currentUser?.uid`, NICHT `effectiveScopeUid`: die Beta-Mitgliedschaft hängt
 *   an der eingeloggten Identität, nicht am Tenant-Scope. Der Scope-Fall
 *   (Advisor im Mandanten-Modus) erreicht diese Komponente ohnehin nie —
 *   `AdvisorHidden` redirectet vorher.
 * - KEIN `<Navigate>`: beide Zweige rendern direkt ⇒ kein Redirect-Loop-Budget,
 *   die URL bleibt für ALLE `/dashboard`. Normale Nutzer sehen byte-gleich das
 *   heutige `InvoiceDashboard`.
 * - Advisor-Ausschluss läuft ausschließlich über das bestehende, unveränderte
 *   `AdvisorHidden`/`AdvisorLandingGate`-Wrapping — hier keine Advisor-Logik.
 */
const DashboardIndexRoute: React.FC = () => {
  const { currentUser } = useAuth();
  return canUseCockpit(currentUser?.uid) ? <CockpitPage /> : <InvoiceDashboard />;
};

/**
 * Pfad → Element-Fabrik. Jeder Eintrag in `DASHBOARD_ROUTE_PATHS` MUSS hier
 * einen Eintrag haben (s. Fail-Fast-Hinweis im Modul-Kopf). Als Funktionen
 * geführt (nicht als bereits instanzierte Elemente), damit
 * `buildDashboardChildren()` bei jedem Aufruf frische Elemente erzeugt.
 */
const ELEMENT_BY_PATH: Readonly<Record<string, () => React.ReactElement>> = {
  // architecture-cockpit-route-split.md §5.2 (E3): Index-Entscheid wandert von
  // der Modul-Ladezeit-Ternary in die Render-Komponente `DashboardIndexRoute`
  // (uid-abhängig via `canUseCockpit`). `AdvisorHidden`-Wrapping bleibt
  // unverändert — ein Mandanten-Modus-Advisor sieht das Cockpit nie.
  // `AdvisorLandingGate` (M-ACF-2, §7.4) wrappt AUSSEN um `AdvisorHidden` — der
  // Einmal-pro-Tab-Session-Redirect eines Advisors ins Kanzlei-Cockpit. NUR am
  // Index; `/dashboard/rechnungen/neu` bewusst OHNE Gate.
  '/dashboard': () => (
    <AdvisorLandingGate>
      <AdvisorHidden>
        <DashboardIndexRoute />
      </AdvisorHidden>
    </AdvisorLandingGate>
  ),
  // architecture-cockpit-route-split.md §3 E1: eigene Formular-Route, jetzt
  // UNBEDINGT registriert (flag-frei, s. `routes/dashboardRoutePaths.ts`).
  // Rendert immer das `InvoiceDashboard`, owner-only (`AdvisorHidden`) — das
  // konstante Ziel von `INVOICE_FORM_ROUTE` (§3 E2).
  '/dashboard/rechnungen/neu': () => (
    <AdvisorHidden>
      <InvoiceDashboard />
    </AdvisorHidden>
  ),
  '/dashboard/archiv': () => <InvoiceArchive />,
  '/dashboard/angebote-archiv': () => (
    <AdvisorHidden>
      <OfferArchive />
    </AdvisorHidden>
  ),
  '/dashboard/angebote': () => (
    <AdvisorHidden>
      <OfferDashboard />
    </AdvisorHidden>
  ),
  '/dashboard/xrechnung': () => <XRechnungDashboard />,
  '/dashboard/abonnement': () => <SubscriptionPage />,
  '/dashboard/abo-erfolg': () => <SubscriptionSuccessPage />,
  '/dashboard/abo-abgebrochen': () => <SubscriptionCancelPage />,
  '/dashboard/konto': () => <AccountPage />,
  '/dashboard/konto/steuerberater': () => <AdvisorPortalPage />,
  // M-UX-7.2: Mandanten-Übersicht. Bewusst KEIN `<AdvisorHidden>`-Wrapper —
  // die Seite muss aus dem Mandanten-Modus heraus („Mandant wechseln", 7.1)
  // UND im Kanzlei-Kontext erreichbar sein (analog `/dashboard/cockpit`).
  '/dashboard/mandanten': () => <MandantenPage />,
  '/dashboard/cockpit': () => <AdvisorCockpitPage />,
  '/dashboard/datev-sammelexport': () => <AdvisorDatevPage />,
  '/dashboard/konto/mandanten-einladen': () => <ClientInvitePage />,
  '/dashboard/konto/kanzlei-zugang': () => <ClientInviteAcceptPage />,
  '/dashboard/kunden': () => (
    <AdvisorHidden>
      <CustomerManagementPage />
    </AdvisorHidden>
  ),
  '/dashboard/produkte': () => (
    <AdvisorHidden>
      <ProductManagementPage />
    </AdvisorHidden>
  ),
  '/dashboard/workflows': () => (
    <AdvisorHidden>
      <WorkflowManagementPage />
    </AdvisorHidden>
  ),
  '/dashboard/workflows/:id': () => (
    <AdvisorHidden>
      <WorkflowDetailPage />
    </AdvisorHidden>
  ),
  '/dashboard/pdf-design': () => <PdfSettingsPage />,
  // M-UX-5.2 (§8 Kompatibilität/Redirects): die drei Alt-Routen bleiben
  // REGISTRIERT (Deep-Link-Erhalt für Bookmarks/externe Links). Hat der
  // AKTUELLE Nutzer Unified-Inbox-Zugriff (`canUseUnifiedInbox`, s.
  // `EingangsrechnungenRoute`/`PosteingangRoute`/`BelegeRoute` oben), rendern
  // sie eine `Navigate` auf die vereinheitlichte Liste mit dem passenden
  // `?stream=`-Parameter statt ihrer bisherigen Seite. Sonst unverändertes
  // Verhalten (Null-Delta) — die Alt-Seiten werden NICHT gelöscht.
  '/dashboard/eingangsrechnungen': () => <EingangsrechnungenRoute />,
  '/dashboard/eingaenge': () => <UnifiedInboxRoute />,
  '/dashboard/posteingang': () => <PosteingangRoute />,
  '/dashboard/offene-posten': () => <OpenItemsPage />,
  '/dashboard/nachrichten': () => <MessagesPage />,
  '/dashboard/ausgaben': () => <ExpensesPage />,
  '/dashboard/wiederkehrend': () => (
    <AdvisorHidden>
      <RecurringInvoicesPage />
    </AdvisorHidden>
  ),
  '/dashboard/datev-export': () => <DatevExportPage />,
  '/dashboard/umsatzsteuer': () => (
    <AdvisorHidden>
      <UmsatzsteuerPage />
    </AdvisorHidden>
  ),
  '/dashboard/gobd-export': () => (
    <AdvisorHidden>
      <GobdExportPage />
    </AdvisorHidden>
  ),
  // GoBD-Verfahrensdokumentations-Seite (Doku-Volltext, NICHT der Z3-Export
  // oben) — Datei/Komponente heißt `GobdDocPage` (s. deren Modul-Kopf zum
  // `legal-gate.ps1`-camelCase-Fehlalarm auf `GobdPage.tsx`).
  '/dashboard/gobd': () => (
    <AdvisorHidden>
      <GobdDocPage />
    </AdvisorHidden>
  ),
  '/dashboard/belege': () => <BelegeRoute />,
  '/dashboard/bank-abgleich': () => (
    <AdvisorHidden>
      <BankReconciliationPage />
    </AdvisorHidden>
  ),
  // RK-7 (architecture-reisekosten.md §7.2): bewusst KEIN `<AdvisorHidden>`-
  // Wrapper — Präzedenz `/dashboard/ausgaben`. Ein aktiver Advisor im Mandanten-
  // Scope sieht die Reisen des Mandanten (Read via Firestore-Rules, §7);
  // Schreib-Aktionen scheitern serverseitig (`canWriteInTenant` schließt
  // 'advisor' aus) — die Seite blendet sie zusätzlich defensiv aus.
  '/dashboard/reisekosten': () => <TravelExpensesPage />,
};

const DASHBOARD_INDEX_PATH = '/dashboard';

/**
 * Baut die `children`-`RouteObject[]` der `/dashboard`-Route — abgeleitet aus
 * `DASHBOARD_ROUTE_PATHS` (eine Quelle, s. Modul-Kopf). Verhaltensidentisch
 * zur vorherigen `App.tsx`-Inline-Definition: identische Elemente, identische
 * Flag-Gates (bereits in `DASHBOARD_ROUTE_PATHS` eingepreist), identisches
 * `errorElement` je Kind-Route.
 *
 * @throws Error wenn ein Pfad aus `DASHBOARD_ROUTE_PATHS` keinen Eintrag in
 *   `ELEMENT_BY_PATH` hat (interner Wartungsfehler dieser Datei — Fail-Fast
 *   statt eines stillen 404 zur Laufzeit).
 */
export function buildDashboardChildren(): RouteObject[] {
  return DASHBOARD_ROUTE_PATHS.map((absolutePath) => {
    const makeElement = ELEMENT_BY_PATH[absolutePath];
    if (!makeElement) {
      throw new Error(
        `buildDashboardChildren: kein Element für Route "${absolutePath}" registriert (ELEMENT_BY_PATH und DASHBOARD_ROUTE_PATHS sind auseinandergelaufen).`,
      );
    }

    if (absolutePath === DASHBOARD_INDEX_PATH) {
      return {
        index: true,
        element: makeElement(),
        errorElement: <ErrorBoundary />,
      };
    }

    const relativePath = absolutePath.slice(`${DASHBOARD_INDEX_PATH}/`.length);
    return {
      path: relativePath,
      element: makeElement(),
      errorElement: <ErrorBoundary />,
    };
  });
}
